<script setup lang="tsx">
import type { CustomCells, HybridlyRow } from '@/components/table/Table';
import type { IndexTestTypeTabNavCounts } from '@/components/testTypes/IndexTestTypeTabNav.vue';

import { ButtonColor, ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';
import { useAuth } from '@/hooks/useAuth';
import { useHybridlyTable } from '@/hooks/useHybridlyTable';
import { useTableRowClick } from '@/hooks/useTableRowClick';

import BulkDeleteButton from '@/components/table/BulkDeleteButton.vue';
import Button from '@/components/button/Button.vue';
import EmptyState from '@/components/emptyState/EmptyState.vue';
import IndexPageLayout from '@/components/pageLayout/IndexPageLayout.vue';
import IndexTestTypeTabNav from '@/components/testTypes/IndexTestTypeTabNav.vue';
import TableBadgesCell from '@/components/table/TableBadgesCell.vue';
import TableDateTimeCell from '@/components/table/TableDateTimeCell.vue';

import FilesLandscapeAlt from '@/icons/line/files-landscapes-alt.svg';
import Plus from '@/icons/line/plus.svg';
import TableTruncatedTextCell from '@/components/table/TableTruncatedTextCell.vue';

type CustomTestRow = HybridlyRow<App.Configuration.Data.CustomTestData>;

const props = defineProps<{
  counts: IndexTestTypeTabNavCounts;
  customTests: Table<App.Configuration.Data.CustomTestData>;
}>();

const { hasPermission } = useAuth();
const { table, tableSelectionState } = useHybridlyTable<App.Configuration.Data.CustomTestData>(
  props,
  'customTests'
);
const { handleRowClick } = useTableRowClick();

const customCells = {
  display_name: ({ row, column }) => <TableTruncatedTextCell text={row.value(column.key)} />,
  program: ({ row, column }) => (
    <TableBadgesCell
      badgesToDisplay={1}
      itemIcon={FilesLandscapeAlt}
      items={[row.value(column.key).display_name]}
    />
  ),
  created_at: ({ row, column }) => <TableDateTimeCell date={row.value(column.key)} />,
  updated_at: ({ row, column }) => <TableDateTimeCell date={row.value(column.key)} />
} as CustomCells<App.Configuration.Data.CustomTestData>;

function handleCustomTestRowClick(event: MouseEvent, row: CustomTestRow) {
  const url = route('custom-tests.show', {
    customTest: row.key
  });
  handleRowClick(url, event);
}
</script>


					<script lang="tsx">
					import __hybridly_layout_0 from '~/resources/layouts/default.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <IndexPageLayout
    title="Custom Tests"
    :table="table"
    :customCells="customCells"
    :stickyEndColumns="1"
    :stickyStartColumns="1"
    :tableSelectionState="tableSelectionState"
    clickableRows
    @clickRow="($event, row) => handleCustomTestRowClick($event, row as CustomTestRow)"
  >
    <template #rightButtons>
      <Button
        v-if="hasPermission('create-custom-test') && !tableSelectionState.hasSelection"
        :color="ButtonColor.primary"
        :variant="ButtonVariant.solid"
        :size="ButtonSize.sm"
        :iconLeft="Plus"
        :href="route('custom-tests.create')"
      >
        Add a custom test
      </Button>

      <BulkDeleteButton
        recordType="Custom Tests"
        :route="route('custom-tests.bulk-delete')"
        :tableSelectionState="tableSelectionState"
      />
    </template>
    <template #tabs>
      <IndexTestTypeTabNav :counts />
    </template>
    <template #emptyState>
      <EmptyState
        title="No custom tests found"
        :description="
          hasPermission('create-custom-test')
            ? 'Create a new custom test by clicking the button below'
            : ''
        "
        :actionLabel="hasPermission('create-custom-test') ? 'Add a custom test' : ''"
        :actionHref="route('custom-tests.create')"
      />
    </template>
  </IndexPageLayout>
</template>
